import { useEffect } from "react";

export interface KeyboardActions {
  onChangeChannel?: () => void;
  onPrevious?: () => void;
  onPlayPause?: () => void;
  onFullscreen?: () => void;
  onMute?: () => void;
  onInfo?: () => void;
  onHistory?: () => void;
  onFavorites?: () => void;
  onMode?: () => void;
  onTimeDial?: () => void;
  onArchive?: () => void;
  onEscape?: () => void;
}

const IGNORE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

export function useKeyboard(actions: KeyboardActions, enabled = true) {
  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && IGNORE_TAGS.has(target.tagName)) return;

      // Space, ArrowRight, "D", "d" — change channel
      if (e.code === "Space" || e.key === " " || e.key === "ArrowRight" || e.key === "d" || e.key === "D") {
        e.preventDefault();
        actions.onChangeChannel?.();
        return;
      }

      // ArrowLeft, "A" — previous
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") {
        e.preventDefault();
        actions.onPrevious?.();
        return;
      }

      // K or "k" — play/pause
      if (e.key === "k" || e.key === "K") {
        e.preventDefault();
        actions.onPlayPause?.();
        return;
      }

      // F or "f" — fullscreen
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        actions.onFullscreen?.();
        return;
      }

      // M or "m" — mute toggle
      if (e.key === "m" || e.key === "M") {
        e.preventDefault();
        actions.onMute?.();
        return;
      }

      // I or "i" — toggle info panel
      if (e.key === "i" || e.key === "I") {
        e.preventDefault();
        actions.onInfo?.();
        return;
      }

      // H or "h" — toggle history
      if (e.key === "h" || e.key === "H") {
        e.preventDefault();
        actions.onHistory?.();
        return;
      }

      // L or "l" — favorites
      if (e.key === "l" || e.key === "L") {
        e.preventDefault();
        actions.onFavorites?.();
        return;
      }

      // R or "r" — open mode selector
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        actions.onMode?.();
        return;
      }

      // T or "t" — time dial
      if (e.key === "t" || e.key === "T") {
        e.preventDefault();
        actions.onTimeDial?.();
        return;
      }

      // O: local viewing archive and blocked tags
      if (e.key === "o" || e.key === "O") {
        e.preventDefault();
        actions.onArchive?.();
        return;
      }

      // Escape
      if (e.key === "Escape") {
        actions.onEscape?.();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [actions, enabled]);
}
